import { useState } from 'react';
import { AlertCircle, AlertTriangle, Building2, CheckCircle, ChevronDown, ChevronUp, PoundSterling, ShieldCheck, TrendingUp, User2 } from 'lucide-react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip as ChartTooltip } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { ComplianceBadge } from '../ComplianceBadge';

const stats = [
  {
    title: 'Properties',
    value: '3',
    subtitle: 'Active portfolio',
    icon: Building2,
    iconClassName: 'text-blue-100',
    valueClassName: 'text-navy-900',
  },
  {
    title: 'Rent Due',
    value: '£3,200',
    subtitle: 'This month',
    icon: PoundSterling,
    iconClassName: 'text-green-100',
    valueClassName: 'text-blue-600',
  },
  {
    title: 'MOT Status',
    value: '2 / 1',
    subtitle: 'Pass / Advisory',
    icon: ShieldCheck,
    iconClassName: 'text-yellow-100',
    valueClassName: 'text-green-600',
  },
];

const properties = [
  {
    id: 'oak-house',
    name: 'Oak House',
    postcode: 'E12 5LN',
    tenants: ['Aisha Khan'],
    rent: '£1,050',
    status: 'pass',
  },
  {
    id: 'elm-court',
    name: 'Elm Court',
    postcode: 'IG1 4BT',
    tenants: ['Daniel Lewis'],
    rent: '£975',
    status: 'pass',
  },
  {
    id: 'cedar-mews',
    name: 'Cedar Mews',
    postcode: 'RM8 2PS',
    tenants: ['Priya Patel', 'Omar Ali'],
    rent: '£1,175',
    status: 'advisory',
  },
];

const PIE_COLORS = ['#6366f1', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6'];

const sa105Breakdown = [
  { category: 'repairs_maintenance', label: 'Repairs & Maintenance', total: 1240, color: PIE_COLORS[0] },
  { category: 'insurance', label: 'Insurance', total: 620, color: PIE_COLORS[1] },
  { category: 'management_fees', label: 'Management Fees', total: 890, color: PIE_COLORS[2] },
  { category: 'mortgage_interest', label: 'Mortgage Interest', total: 1450, color: PIE_COLORS[3] },
  { category: 'ground_rent_service_charge', label: 'Ground Rent / Service Charge', total: 380, color: '#14b8a6' },
  { category: 'legal_professional', label: 'Legal & Professional', total: 290, color: '#f97316' },
  { category: 'utilities', label: 'Utilities', total: 340, color: '#ec4899' },
  { category: 'travel', label: 'Travel', total: 120, color: '#06b6d4' },
  { category: 'other_expenses', label: 'Other Expenses', total: 430, color: PIE_COLORS[4] },
];

const chartTooltipFormatter = (value) => {
  const amount = Number(value || 0).toLocaleString('en-GB');
  return [`£${amount}`, 'Amount'];
};

const getTenantSummary = (property) => {
  const tenants = Array.isArray(property.tenants) ? property.tenants.filter(Boolean) : [];

  if (!tenants.length) {
    return {
      primary: 'No tenant assigned',
      secondary: '',
    };
  }

  return {
    primary: tenants.join(', '),
    secondary: '',
  };
};

const getMotBadgeConfig = (status) => {
  const normalizedStatus = typeof status === 'string' ? status.toLowerCase() : '';

  if (normalizedStatus === 'advisory' || normalizedStatus === 'warning') {
    return {
      label: 'MOT Advisory',
      shortLabel: 'Advisory',
      className: 'border-amber-200 bg-amber-50 text-amber-700',
      icon: AlertTriangle,
    };
  }

  if (normalizedStatus === 'fail' || normalizedStatus === 'non-compliant' || normalizedStatus === 'due') {
    return {
      label: 'MOT Due',
      shortLabel: 'Due',
      className: 'border-red-200 bg-red-50 text-red-700',
      icon: AlertCircle,
    };
  }

  if (normalizedStatus === 'bypassed' || normalizedStatus === 'disabled') {
    return {
      label: 'MOT Disabled',
      shortLabel: 'Disabled',
      className: 'border-slate-200 bg-slate-100 text-slate-700',
      icon: AlertTriangle,
    };
  }

  return {
    label: 'MOT Pass',
    shortLabel: 'Pass',
    className: 'border-green-200 bg-green-50 text-green-700',
    icon: CheckCircle,
  };
};

const CompactMotBadge = ({ status, mobile = false }) => {
  const config = getMotBadgeConfig(status);
  const Icon = config.icon;

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-medium ${config.className} ${mobile ? 'text-[11px]' : 'text-xs'}`}
    >
      <Icon className={mobile ? 'h-3 w-3' : 'h-3.5 w-3.5'} />
      <span>{mobile ? config.shortLabel : config.label}</span>
    </span>
  );
};

const mobileStats = stats.slice(0, 2);
const mobileProperties = properties;
const motPassCount = properties.filter((property) => property.status === 'pass').length;
const motAdvisoryCount = properties.filter((property) => property.status === 'advisory' || property.status === 'warning').length;
const motDueCount = properties.filter((property) => ['due', 'fail', 'non-compliant'].includes(String(property.status || '').toLowerCase())).length;
const totalMotProperties = properties.length || 1;
const motPassSweep = (motPassCount / totalMotProperties) * 360;
const motAdvisorySweep = (motAdvisoryCount / totalMotProperties) * 360;
const motPassPercent = Math.round((motPassCount / totalMotProperties) * 100);

const propertyMarketRates = [
  { id: 'oak-house', name: 'Oak House', postcode: 'E12 5LN', currentRent: 1050, min: 875, median: 1020, max: 1250 },
  { id: 'elm-court', name: 'Elm Court', postcode: 'IG1 4BT', currentRent: 975, min: 850, median: 1000, max: 1175 },
  { id: 'cedar-mews', name: 'Cedar Mews', postcode: 'RM8 2PS', currentRent: 1175, min: 950, median: 1100, max: 1400 },
];

const PropertyRentRow = ({ property, compact }) => {
  const { name, postcode, currentRent, min, median, max } = property;
  const range = max - min;
  const currentPct = range > 0 ? Math.min(Math.max(((currentRent - min) / range) * 100, 2), 98) : 50;
  const medianPct = range > 0 ? Math.min(Math.max(((median - min) / range) * 100, 2), 98) : 50;
  const vsMedian = currentRent - median;
  const isAbove = vsMedian > 0;

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className={`font-medium text-navy-900 truncate ${compact ? 'text-[11px]' : 'text-xs'}`}>{name}</p>
          <p className={`text-muted-foreground ${compact ? 'text-[9px]' : 'text-[10px]'}`}>{postcode}</p>
        </div>
        <div className="text-right shrink-0">
          <p className={`font-bold text-navy-900 ${compact ? 'text-xs' : 'text-sm'}`}>£{currentRent.toLocaleString('en-GB')}</p>
          <p className={`font-medium ${isAbove ? 'text-green-600' : 'text-amber-600'} ${compact ? 'text-[9px]' : 'text-[10px]'}`}>
            {isAbove ? '+' : ''}£{vsMedian.toLocaleString('en-GB')} vs median
          </p>
        </div>
      </div>
      <div className="relative h-1.5 rounded-full bg-muted">
        <div
          className="absolute top-1/2 -translate-y-1/2 h-1.5 rounded-full bg-slate-300"
          style={{ left: 0, right: 0 }}
        />
        <div
          className="absolute top-1/2 -translate-y-1/2 h-3 w-0.5 rounded-full bg-slate-400"
          style={{ left: `${medianPct}%` }}
          title={`Median £${median.toLocaleString('en-GB')}`}
        />
        <div
          className="absolute top-1/2 -translate-y-1/2 h-3.5 w-3.5 rounded-full bg-royal-600 border-2 border-white shadow z-10"
          style={{ left: `calc(${currentPct}% - 7px)` }}
          title={`Your rent £${currentRent.toLocaleString('en-GB')}`}
        />
      </div>
      <div className={`flex justify-between text-muted-foreground ${compact ? 'text-[9px]' : 'text-[10px]'}`}>
        <span>£{min.toLocaleString('en-GB')}</span>
        <span>£{max.toLocaleString('en-GB')}</span>
      </div>
    </div>
  );
};

const MarketRateMockCard = ({ compact = false }) => (
  <Card className={`border-slate-200 shadow-sm ${compact ? 'min-w-[76%] snap-center' : 'shrink-0 w-[280px] snap-start'}`}>
    <CardHeader className="pb-2">
      <div className="flex items-center justify-between gap-2">
        <CardTitle className={compact ? 'text-sm' : 'text-base'}>Market rate</CardTitle>
        <TrendingUp className={`text-royal-500 ${compact ? 'h-3.5 w-3.5' : 'h-4 w-4'}`} />
      </div>
      <p className={`text-muted-foreground ${compact ? 'text-[10px]' : 'text-xs'}`}>
        Your rent vs local market · illustrative
      </p>
    </CardHeader>
    <CardContent className="space-y-4 pt-0">
      {propertyMarketRates.map((property) => (
        <PropertyRentRow key={property.id} property={property} compact={compact} />
      ))}
      {!compact && (
        <p className="text-[10px] text-muted-foreground">
          Illustrative data only. Not a professional rental valuation.
        </p>
      )}
    </CardContent>
  </Card>
);

export const DashboardMockup = () => {
  const [showMobileDetails, setShowMobileDetails] = useState(false);

  return (
    <div className="mx-auto w-full max-w-[320px] px-1 [transform:perspective(900px)_rotateY(-4deg)_rotateX(3deg)] transform-gpu sm:max-w-[560px] sm:px-0 sm:[transform:perspective(1000px)_rotateY(-6deg)_rotateX(3deg)_scale(0.96)] lg:max-w-[780px] lg:[transform:perspective(1200px)_rotateY(-8deg)_rotateX(4deg)] xl:max-w-[840px]">
      <div className="overflow-hidden rounded-[22px] border border-white/15 bg-white shadow-[0_20px_56px_rgba(2,6,23,0.24)] ring-1 ring-navy-900/5 sm:rounded-[26px] sm:shadow-[0_24px_80px_rgba(2,6,23,0.32)] lg:rounded-[28px]">
        <div className="border-b border-slate-200 bg-gradient-to-r from-white via-white to-navy-50/70 px-4 py-3.5 sm:px-6 sm:py-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-royal-600">Landlord dashboard</p>
              <h3 className="mt-1 font-heading text-lg font-semibold text-navy-900 sm:text-xl">Portfolio overview</h3>
            </div>
            <div className="hidden sm:flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-muted-foreground shadow-sm">
              <span className="h-2 w-2 rounded-full bg-success" />
              Live snapshot
            </div>
          </div>
        </div>

        <div className="space-y-4 bg-slate-50/70 p-3.5 sm:space-y-5 sm:p-5">
          <div className="hidden gap-3 sm:grid min-[420px]:grid-cols-3">
            {stats.map((stat) => (
              <Card key={stat.title} className="border-slate-200 shadow-sm">
                <CardContent className="px-4 pb-4 pt-4 sm:pt-6">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-xs font-medium text-gray-600 sm:text-sm">{stat.title}</p>
                      <p className={`mt-1.5 text-2xl font-bold sm:mt-2 sm:text-3xl ${stat.valueClassName}`}>{stat.value}</p>
                      <p className="mt-1 text-xs text-muted-foreground">{stat.subtitle}</p>
                    </div>
                    <stat.icon className={`h-8 w-8 sm:h-10 sm:w-10 ${stat.iconClassName}`} />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="space-y-3 sm:hidden">
            <div className="overflow-hidden rounded-[20px] bg-gradient-to-br from-navy-900 via-navy-800 to-royal-700 px-3.5 py-4 text-white shadow-[0_16px_40px_rgba(15,23,42,0.28)]">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/70">Mobile snapshot</p>
                  <p className="mt-1 font-heading text-lg font-semibold">Portfolio in your pocket</p>
                </div>
                <span className="inline-flex items-center justify-center rounded-full border border-white/15 bg-white/10 px-2 py-1 text-center text-[10px] font-medium text-white/85">
                  Live view
                </span>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-2.5">
                {mobileStats.map((stat) => (
                  <div key={stat.title} className="rounded-2xl border border-white/10 bg-white/10 px-3 py-3 backdrop-blur-sm">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-white/70">{stat.title}</p>
                      <stat.icon className="h-4 w-4 text-white/70" />
                    </div>
                    <p className="mt-2 text-xl font-bold text-white">{stat.value}</p>
                    <p className="mt-1 text-[11px] text-white/70">{stat.subtitle}</p>
                  </div>
                ))}
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-1 rounded-full border border-green-300/20 bg-green-400/10 px-2.5 py-1 text-[11px] font-medium text-green-100">
                  <CheckCircle className="h-3 w-3" />
                  {motPassCount} pass
                </span>
                <span className="inline-flex items-center gap-1 rounded-full border border-amber-300/20 bg-amber-400/10 px-2.5 py-1 text-[11px] font-medium text-amber-100">
                  <AlertTriangle className="h-3 w-3" />
                  {motAdvisoryCount} advisory
                </span>
                <span className="inline-flex items-center gap-1 rounded-full border border-white/15 bg-white/10 px-2.5 py-1 text-[11px] font-medium text-white/85">
                  <ShieldCheck className="h-3 w-3" />
                  MOT tracked
                </span>
              </div>

              <button
                type="button"
                onClick={() => setShowMobileDetails((current) => !current)}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-white/15 bg-white/10 px-3 py-2.5 text-sm font-medium text-white transition-colors hover:bg-white/15"
                aria-expanded={showMobileDetails}
              >
                {showMobileDetails ? 'Hide dashboard details' : 'Preview dashboard details'}
                {showMobileDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
            </div>

            {showMobileDetails ? (
            <>
            <div>
              <div className="mb-2 flex items-center justify-between gap-2 px-0.5">
                <p className="text-sm font-semibold text-navy-900">Portfolio</p>
                <p className="text-[11px] font-medium text-muted-foreground">Swipe cards</p>
              </div>

              <div className="-mx-1 flex snap-x snap-mandatory gap-3 overflow-x-auto px-1 pb-1 no-scrollbar">
                {mobileProperties.map((property) => (
                  (() => {
                    const tenantSummary = getTenantSummary(property);
                    return (
                  <div
                    key={property.id}
                    className="min-w-[85%] snap-center rounded-[20px] border border-slate-200 bg-white p-3.5 shadow-sm"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-royal-50 text-royal-600">
                            <Building2 className="h-4 w-4" />
                          </span>
                          <div className="min-w-0">
                            <p className="truncate font-heading text-sm font-semibold text-navy-900">{property.name}</p>
                            <p className="text-[11px] text-muted-foreground">{property.postcode}</p>
                          </div>
                        </div>
                      </div>
                      <CompactMotBadge status={property.status} mobile />
                    </div>

                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <div className="rounded-2xl bg-slate-50 px-3 py-2.5">
                        <p className="text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground">Tenant</p>
                        <div className="mt-1 flex items-center gap-1.5 text-xs font-medium text-navy-900">
                          <User2 className="h-3 w-3 text-navy-400" />
                          <span className="truncate">{tenantSummary.primary}</span>
                        </div>
                        {tenantSummary.secondary ? (
                          <p className="mt-1 text-[10px] font-medium text-royal-600">{tenantSummary.secondary}</p>
                        ) : null}
                      </div>
                      <div className="rounded-2xl bg-slate-50 px-3 py-2.5 text-right">
                        <p className="text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground">Monthly rent</p>
                        <p className="mt-1 text-sm font-bold text-navy-900">{property.rent}</p>
                      </div>
                    </div>
                  </div>
                    );
                  })()
                ))}
              </div>
            </div>

            <div>
              <div className="mb-2 flex items-center justify-between gap-2 px-0.5">
                <p className="text-sm font-semibold text-navy-900">Finances, Market & MOT </p>
                <p className="text-[11px] font-medium text-muted-foreground">Swipe cards</p>
              </div>

              <div className="-mx-1 flex snap-x snap-mandatory gap-3 overflow-x-auto px-1 pb-1 no-scrollbar">
              <Card className="min-w-[85%] snap-center border-slate-200 shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">SA105</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <ResponsiveContainer width="100%" height={136}>
                    <PieChart>
                      <Pie
                        data={sa105Breakdown}
                        dataKey="total"
                        nameKey="label"
                        cx="46%"
                        cy="50%"
                        innerRadius={24}
                        outerRadius={36}
                        label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                        labelLine={false}
                        stroke="none"
                      >
                        {sa105Breakdown.map((item) => (
                          <Cell key={item.category} fill={item.color} />
                        ))}
                      </Pie>
                      <ChartTooltip formatter={chartTooltipFormatter} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="mt-2 space-y-1.5">
                    {sa105Breakdown.map((item) => (
                      <div key={item.category} className="flex items-center justify-between gap-2 text-[10px]">
                        <span className="inline-flex min-w-0 flex-1 items-center gap-1.5 text-muted-foreground">
                          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: item.color }} />
                          <span className="truncate">{item.label}</span>
                        </span>
                        <span className="shrink-0 font-medium text-navy-900">£{item.total.toLocaleString('en-GB')}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <MarketRateMockCard compact />

              <Card className="min-w-[76%] snap-center border-slate-200 shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">MOT pulse</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 pt-0">
                  <div className="flex items-center gap-3 rounded-2xl bg-slate-50 px-3 py-3">
                    <div
                      className="relative h-20 w-20 shrink-0 rounded-full"
                      style={{
                        background: `conic-gradient(#22c55e 0deg ${motPassSweep}deg, #f59e0b ${motPassSweep}deg ${motPassSweep + motAdvisorySweep}deg, #ef4444 ${motPassSweep + motAdvisorySweep}deg 360deg)`,
                      }}
                    >
                      <div className="absolute inset-[9px] flex flex-col items-center justify-center rounded-full bg-white text-center">
                        <p className="text-lg font-bold text-navy-900">{motPassPercent}%</p>
                        <p className="text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground">Pass</p>
                      </div>
                    </div>

                    <div className="min-w-0 flex-1 space-y-1.5">
                      <p className="text-xs font-semibold text-navy-900">Portfolio MOT snapshot</p>
                      <div className="flex items-center justify-between gap-1 text-[11px]">
                        <span className="inline-flex min-w-0 items-center gap-1.5 text-muted-foreground truncate">
                          <span className="h-2 w-2 shrink-0 rounded-full bg-green-500" />
                          Pass
                        </span>
                        <span className="shrink-0 font-semibold text-navy-900">{motPassCount}</span>
                      </div>
                      <div className="flex items-center justify-between gap-1 text-[11px]">
                        <span className="inline-flex min-w-0 items-center gap-1.5 text-muted-foreground truncate">
                          <span className="h-2 w-2 shrink-0 rounded-full bg-amber-500" />
                          Advisory
                        </span>
                        <span className="shrink-0 font-semibold text-navy-900">{motAdvisoryCount}</span>
                      </div>
                      <div className="flex items-center justify-between gap-1 text-[11px]">
                        <span className="inline-flex min-w-0 items-center gap-1.5 text-muted-foreground truncate">
                          <span className="h-2 w-2 shrink-0 rounded-full bg-red-500" />
                          Due
                        </span>
                        <span className="shrink-0 font-semibold text-navy-900">{motDueCount}</span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    {properties.map((property) => {
                      const badge = getMotBadgeConfig(property.status);
                      const Icon = badge.icon;
                      return (
                        <div key={`mot-mobile-${property.id}`} className="flex items-center justify-between gap-2 rounded-xl border border-slate-200 px-2.5 py-2">
                          <div className="min-w-0">
                            <p className="truncate text-xs font-semibold text-navy-900">{property.name}</p>
                            <p className="text-[10px] text-muted-foreground">{property.postcode}</p>
                          </div>
                          <span className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-1 text-[10px] font-medium ${badge.className}`}>
                            <Icon className="h-3 w-3" />
                            {badge.shortLabel}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
              </div>
            </div>
            </>
            ) : null}
          </div>

          <div className="hidden sm:flex gap-4 overflow-x-auto pb-1 snap-x snap-mandatory">
            <Card className="shrink-0 w-[340px] border-slate-200 shadow-sm snap-start">
              <CardHeader className="pb-2.5 sm:pb-3">
                <CardTitle className="text-base">Properties</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2.5 pt-0">
                {properties.map((property) => (
                  (() => {
                    const tenantSummary = getTenantSummary(property);
                    return (
                  <div
                    key={property.id}
                    className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="truncate font-heading text-sm font-semibold text-navy-900">{property.name}</p>
                          <span className="rounded-full bg-navy-100 px-2 py-0.5 text-[11px] font-medium text-navy-700">
                            {property.postcode}
                          </span>
                        </div>
                        <div className="mt-1.5 flex items-center gap-2 text-xs text-muted-foreground">
                          <User2 className="h-3.5 w-3.5 text-navy-400" />
                          <span>{tenantSummary.primary}</span>
                        </div>
                        {tenantSummary.secondary ? (
                          <p className="mt-1 text-xs font-medium text-royal-600">{tenantSummary.secondary}</p>
                        ) : null}
                      </div>
                      <div className="flex flex-col items-end gap-2 shrink-0">
                        <ComplianceBadge status={property.status} />
                        <div className="text-right">
                          <p className="text-base font-bold text-navy-900">{property.rent}</p>
                          <p className="text-xs text-muted-foreground">Monthly rent</p>
                        </div>
                      </div>
                    </div>
                  </div>
                    );
                  })()
                ))}
              </CardContent>
            </Card>

            <Card className="shrink-0 w-[300px] overflow-hidden border-slate-200 shadow-sm snap-start">
              <CardHeader className="pb-2.5 sm:pb-3">
                <CardTitle className="text-base">SA105 categories</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <p className="mb-3 text-xs text-muted-foreground">Illustrative SA105 expense split.</p>
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie
                      data={sa105Breakdown}
                      dataKey="total"
                      nameKey="label"
                      cx="46%"
                      cy="50%"
                      innerRadius={38}
                      outerRadius={56}
                      label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                      stroke="none"
                    >
                      {sa105Breakdown.map((item) => (
                        <Cell key={item.category} fill={item.color} />
                      ))}
                    </Pie>
                    <ChartTooltip formatter={chartTooltipFormatter} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="mt-3 space-y-2">
                  {sa105Breakdown.map((item) => (
                    <div key={item.category} className="flex items-center justify-between gap-3 text-xs">
                      <span className="inline-flex min-w-0 items-center gap-2 text-muted-foreground">
                        <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: item.color }} />
                        <span className="truncate">{item.label}</span>
                      </span>
                      <span className="font-medium text-navy-900">£{item.total.toLocaleString('en-GB')}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <MarketRateMockCard />
          </div>
        </div>
      </div>
    </div>
  );
};
